import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RequestCard from "../components/RequestCard";
import { CATEGORY_IDS, CATEGORY_ICONS, BADGE_EMOJIS } from "../lib/constants";
import { ArrowRight, PlusCircle, Search, BellRing } from "lucide-react";
import { useT } from "../i18n/I18nContext";

export default function HomeApp() {
  const { user } = useAuth();
  const t = useT();
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/requests", { params: { status: "open" } })
      .then(({ data }) => { if (!cancelled) setRequests(data.slice(0, 6)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const verified = user?.email_verified;
  const myBadges = user?.badges || [];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 anim-fade-up" data-testid="home-page">
      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-mabw p-8 md:p-10 bg-gradient-to-br from-white to-stone-50">
          <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-3">
            {t("home.hello", { name: user?.first_name })}
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>
            {t("home.title")}
          </h1>
          <p className="text-mabw-soft mt-3 max-w-xl">{t("home.sub")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/app/nuova" data-testid="home-new-btn" className="btn-mabw">
              <PlusCircle className="w-4 h-4" /> {t("home.new_btn")}
            </Link>
            <Link to="/app/cerca" data-testid="home-browse-btn" className="btn-mabw-secondary">
              <Search className="w-4 h-4" /> {t("home.browse_btn")}
            </Link>
          </div>

          {!verified && (
            <div className="mt-6 flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm">
              <BellRing className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <div className="font-medium text-amber-800">{t("home.unverified_title")}</div>
                <div className="text-amber-700 text-xs mt-0.5">{t("home.unverified_desc")}</div>
              </div>
            </div>
          )}
        </div>

        <div className="card-mabw p-6">
          <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold">{t("home.impact")}</div>
          <div className="mt-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-mabw-soft text-sm">{t("home.completed")}</span>
              <span className="text-2xl font-semibold text-mabw-primary" style={{ fontFamily: "Outfit" }}>{user?.completed_helps ?? 0}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-mabw-soft text-sm">{t("home.avg_rating")}</span>
              <span className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                {user?.ratings_count ? user.avg_rating.toFixed(1) : "—"}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-mabw-soft text-sm">{t("home.reviews")}</span>
              <span className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{user?.ratings_count ?? 0}</span>
            </div>
          </div>
          <div className="mt-5 border-t border-mabw pt-4">
            <div className="text-xs uppercase tracking-[0.18em] text-mabw-soft font-semibold mb-3">{t("home.badges")}</div>
            <div className="flex flex-wrap gap-2">
              {myBadges.length === 0 && <span className="text-sm text-mabw-soft">{t("home.no_badges")}</span>}
              {myBadges.map((id) => (
                <span key={id} title={t(`badges.${id}.desc`)} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 text-sm">
                  <span>{BADGE_EMOJIS[id]}</span> {t(`badges.${id}.name`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-3">{t("home.explore_cat")}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {CATEGORY_IDS.map((id) => {
            const Icon = CATEGORY_ICONS[id];
            return (
              <Link key={id} to={`/app/cerca?cat=${id}`} data-testid={`home-cat-${id}`}
                className="card-mabw p-4 text-center hover:-translate-y-0.5 transition-transform">
                <Icon className="w-5 h-5 mx-auto text-mabw-primary" />
                <div className="text-sm font-medium mt-2">{t(`categories.${id}.label`)}</div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mt-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">
              {t("home.open_requests_overline")}
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("home.open_requests_title")}
            </h2>
          </div>
          <Link to="/app/cerca" className="text-sm text-mabw-primary inline-flex items-center gap-1" data-testid="home-see-all">
            {t("home.see_all")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {requests.length === 0 ? (
          <div className="card-mabw p-10 text-center text-mabw-soft" data-testid="home-empty">
            {t("home.empty")}
            <div className="mt-4">
              <Link to="/app/nuova" className="btn-mabw">{t("home.empty_cta")}</Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {requests.map((r) => <RequestCard key={r.id} request={r} />)}
          </div>
        )}
      </section>
    </div>
  );
}
